import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HandCoins, FloppyDisk } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";

const Row = ({ title, sub, testid, children }) => (
  <div className="flex items-center justify-between gap-4 rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3" data-testid={testid}>
    <div>
      <div className="text-sm font-semibold text-zinc-100">{title}</div>
      <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>
    </div>
    {children}
  </div>
);

export default function CommissionSettings() {
  const [s, setS] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/commissions").then(({ data }) => {
      setS(data);
      setSaved(data);
    });
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/commissions", s);
      setS(data);
      setSaved(data);
      toast.success("Commission settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    }
    setBusy(false);
  };

  if (!s) return null;
  const dirty = JSON.stringify(s) !== JSON.stringify(saved);
  const trig = (v, label, sub) => (
    <button
      type="button"
      data-testid={`commission-trigger-${v}`}
      onClick={() => setS({ ...s, trigger: v })}
      className={`flex-1 rounded-lg border p-3 text-left transition-colors ${
        s.trigger === v ? "border-indigo-500/60 bg-indigo-500/10" : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
      }`}
    >
      <div className="text-sm font-semibold text-zinc-100">{label}</div>
      <div className="mt-0.5 text-xs text-zinc-500">{sub}</div>
    </button>
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Commission settings" />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300">
            <HandCoins size={20} weight="bold" />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Commissions</h1>
            <p className="text-sm text-zinc-500">Shop-wide rules for organization commissions. Each organization's rate lives on its profile.</p>
          </div>
        </div>

        <section className="mt-8 space-y-3">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400">When is commission earned?</h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            {trig("accept", "When the quote is accepted", "Credit lands as soon as the deal is signed")}
            {trig("paid", "When the invoice is paid in full", "Credit lands only after the customer has paid everything")}
          </div>
        </section>

        <section className="mt-8 space-y-2">
          <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-400">Staff tools</h2>
          <Row title="Monthly statements" sub="Download or email an organization's commission statement PDF for any month" testid="commission-setting-statement">
            <Switch data-testid="commission-statement-toggle" checked={!!s.statement_enabled} onCheckedChange={(v) => setS({ ...s, statement_enabled: v })} />
          </Row>
          <Row title="Apply credit to invoices" sub="Let staff apply an organization's commission credit against one of its open invoices" testid="commission-setting-apply">
            <Switch data-testid="commission-apply-toggle" checked={!!s.apply_credit_enabled} onCheckedChange={(v) => setS({ ...s, apply_credit_enabled: v })} />
          </Row>
          <Row title="Quote commission preview" sub="Show the expected commission on quote review so sales sees what the organization earns" testid="commission-setting-preview">
            <Switch data-testid="commission-preview-toggle" checked={!!s.quote_preview_enabled} onCheckedChange={(v) => setS({ ...s, quote_preview_enabled: v })} />
          </Row>
        </section>

        <div className="mt-8">
          <Button data-testid="commission-settings-save" onClick={save} disabled={!dirty || busy} className="h-11 gap-2 bg-indigo-500 px-6 text-xs font-bold uppercase tracking-widest hover:bg-indigo-400">
            <FloppyDisk size={16} weight="bold" /> {busy ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </main>
    </div>
  );
}
